import { useState } from 'react';

import { useFormContext } from '@components/form';
import { FormInstance } from '@components/form/create-form';
import { HideContainer } from '@components/hide-container';
import { SelectOption } from '@components/select';
import { Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { FieldMappingVariables, FieldType } from '@components/text-editors/addons/field-mapping';
import { useScopedTranslate } from '@locale/translate';
import { hourList, minuteList } from '@utils/number-arrays';

import { TimeSavedData } from 'api/journeys/requests';
import { JourneyActionDetailed, TravellerJourneyActionDetailed } from 'api/types';
import { ExecutionMethod } from 'ui/journey-builder/execution-method';
import { DescriptionFormField, useVariableGroups } from 'ui/journey-builder/node-types/common';

import { ActionTimeSelector, TriggerMethod } from '../common';
import { FormGroupContainer } from '../duration';
import { getFieldMappingId } from '../helpers';
import { EmailAddressOptions, SendFromEmailSelect } from '../shared/send-from-email-select';
import { ActionTimeWithTimezoneTooltip } from '../timezone';

export type ActionType = 'calendar_event';

export type CalendarEventData = {
  description: string;
  executionMethod: TriggerMethod;
  title: FieldMappingVariables;
  eventDescription?: FieldMappingVariables;
  location?: FieldMappingVariables;
  organizer?: FieldMappingVariables;
  date: Date;
  hour: string;
  minute: string;
  durationHours?: string;
  durationMinutes?: string;
  attendees?: string[];
  isAutomatic: boolean;
  assignees: SelectOption[];
  sendEmailToAttendees: boolean;
  sendFromEmailId?: string;
} & TimeSavedData;

type CalendarEventFormProps = {
  Form: FormInstance<CalendarEventData>;
  emailAddressOptions: EmailAddressOptions;
  data: JourneyActionDetailed<ActionType> | TravellerJourneyActionDetailed<ActionType>;
};

const isTravellerData = (
  data: CalendarEventFormProps['data'],
): data is TravellerJourneyActionDetailed<ActionType> => {
  return Boolean((data as TravellerJourneyActionDetailed<ActionType>).journeyId);
};

export const CalendarEventFormFields = ({
  Form,
  emailAddressOptions,
  data,
}: CalendarEventFormProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const values = data?.values;
  const { store } = useFormContext();
  const journeyId = isTravellerData(data) ? data.journeyId : data.journey;
  const fieldMappingId = getFieldMappingId(journeyId);

  const defaultCheckboxValue = values?.sendEmailToAttendees?.value ?? true;
  const [isChecked, setIsChecked] = useState(defaultCheckboxValue);

  const hour = (store.useFieldValue('hour') ?? '12') as string;
  const minute = (store.useFieldValue('minute') ?? '0') as string;
  const [offset] = data?.timezone?.split(' ') ?? [''];

  function useVariableGroupsByTypes(types?: FieldType) {
    return useVariableGroups({
      actionVariables: data?.actionVariables,
      journeyId,
      ...(types && { types: [types] }),
      excludedTypes: ['file', 'file_multi'],
    });
  }

  const baseVariableGroups = useVariableGroupsByTypes();
  const emailVariableGroups = useVariableGroupsByTypes('email');
  const dateVariableGroups = useVariableGroupsByTypes('date');

  return (
    <Stack orientation="vertical" space={8}>
      <HideContainer title={t('actionDetails')}>
        <DescriptionFormField defaultValue={data.description} />
      </HideContainer>
      <Sidebar.Separator />
      <HideContainer title={t('triggerMethod')}>
        <ExecutionMethod
          assignees={data.assignees}
          isAutomatic={data.isAutomatic}
          formStore={store}
          hideLabel
        />
      </HideContainer>
      <Sidebar.Separator />
      <HideContainer title={t('calendarEventContent')}>
        <Stack orientation="vertical" space={4}>
          <Form.FieldMappingTextEditor
            id="title"
            label={t('title')}
            plaintext
            singleLine
            defaultValue={values?.title}
            validators={['required']}
            fieldMappingId={fieldMappingId}
            variableGroups={baseVariableGroups}
          />
          <Form.FieldMappingTextEditor
            id="eventDescription"
            label={t('description')}
            plaintext
            defaultValue={values?.description}
            fieldMappingId={fieldMappingId}
            variableGroups={baseVariableGroups}
          />
          <Form.FieldMappingTextEditor
            id="location"
            label={t('location')}
            plaintext
            singleLine
            defaultValue={values?.location}
            fieldMappingId={fieldMappingId}
            variableGroups={baseVariableGroups}
          />
          <Form.EmailField
            id="organizer"
            label={t('organizer')}
            defaultValue={values?.organizer}
            variableGroups={emailVariableGroups}
            helpText={t('emailHelpTextInParentheses')}
            fieldMappingId={fieldMappingId}
          />
          <Form.FieldMappingDatePicker
            id={'date'}
            label={t('date')}
            defaultValue={values?.date}
            variableGroups={dateVariableGroups}
            validators={['required']}
            fieldMappingId={fieldMappingId}
          />
          <ActionTimeSelector
            defaultHour={values?.hour?.value ?? '12'}
            defaultMinute={values?.minute?.value ?? '0'}
          />
          <ActionTimeWithTimezoneTooltip
            actionText={t('eventStartsAt', {
              hour: hour.padStart(2, '0'),
              minute: minute.padStart(2, '0'),
            })}
            hour={hour}
            minute={minute}
            journeyTimezoneOffset={offset}
          />
          <Text font="semibold14" text={t('selectDuration')} />
          <FormGroupContainer>
            <Form.Select
              id="durationHours"
              label={t('hours')}
              options={hourList}
              placeholder=""
              defaultValue={values?.durationHours?.value ?? '1'}
              isClearable={false}
              validators={['required']}
            />
            <Form.Select
              id="durationMinutes"
              label={t('minutes')}
              options={minuteList}
              placeholder=""
              defaultValue={values?.durationMinutes?.value ?? '0'}
              isClearable={false}
              validators={['required']}
            />
          </FormGroupContainer>
          <Form.EmailListField
            id="attendees"
            label={t('attendees')}
            variableGroups={emailVariableGroups}
            defaultValue={values?.attendees}
            validators={isChecked ? ['required'] : []}
            hasResetError={!isChecked}
            helpText={t('emailHelpTextInParentheses')}
            fieldMappingId={fieldMappingId}
          />
          <Form.Checkbox
            id="sendEmailToAttendees"
            label={t('sendAttendeesEmail')}
            defaultValue={defaultCheckboxValue as boolean}
            onChange={setIsChecked}
          />
          {isChecked && (
            <SendFromEmailSelect
              Form={Form}
              emailAddressOptions={emailAddressOptions}
              defaultValue={values?.sendFromEmailId}
            />
          )}
        </Stack>
      </HideContainer>
    </Stack>
  );
};

export const formatFormData = (data: CalendarEventData) => {
  return {
    description: data.description,
    isAutomatic: data.executionMethod === 'automatic',
    action: 'calendar_event',
    assignees: data.executionMethod === 'manual' ? data.assignees.map((user) => user.value) : [],
    values: {
      title: data.title,
      description: data.eventDescription,
      location: data.location,
      organizer: data.organizer,
      date: data.date,
      hour: { value: data.hour },
      minute: { value: data.minute },
      durationHours: { value: data.durationHours },
      durationMinutes: { value: data.durationMinutes },
      attendees: data.attendees ?? [],
      sendEmailToAttendees: { value: data.sendEmailToAttendees },
      sendFromEmailId: data.sendFromEmailId,
    },
    timeSaved: {
      days: data.days,
      hours: data.hours,
      minutes: data.minutes,
    },
  };
};

const sidebarLocale = {
  en: {
    actionDetails: 'Action details',
    triggerMethod: 'Trigger method',
    calendarEventContent: 'Calendar event content',
    title: 'Title',
    description: 'Description',
    location: 'Location',
    organizer: 'Organizer',
    date: 'Date',
    duration: 'Duration',
    days: 'Days',
    hours: 'Hours',
    minutes: 'Minutes',
    attendees: 'Attendees',
    selectDuration: 'Select duration',
    sendAttendeesEmail: 'Send email to attendees?',
    sendFrom: 'Send from',
    addAddress: 'Add email address',
    emailHelpTextInParentheses: '(email)',
    eventStartsAt: 'Event starts at {hour}:{minute}',
  },
};
